import React from 'react';
import { Pencil, Info } from 'lucide-react';
import './InfoPanel.css';

export interface InfoItem {
    label: string;
    /** An element for values with inline controls (a link, a small button). */
    value: string | number | string[] | React.ReactElement;
    valueColor?: string;
    /** When set, renders a pencil edit icon next to the value */
    onEdit?: () => void;
    /** Tooltip/aria label for the edit icon (defaults to "Ändra") */
    editTitle?: string;
    /** Greys the pencil out; `editTitle` then says why */
    editDisabled?: boolean;
    /** When set, renders an info icon next to the value with this text as tooltip */
    info?: string;
    /** Rendered right after the value, before the info and edit icons (e.g. a verified check) */
    valueIcon?: React.ReactNode;
}

interface InfoPanelProps {
    items: InfoItem[];
    className?: string;
}

const InfoPanel: React.FC<InfoPanelProps> = ({ items, className = '' }) => {
    const renderValue = (value: InfoItem['value']) => {
        if (Array.isArray(value)) {
            return value.map((item, idx) => (
                <React.Fragment key={idx}>
                    {item}
                    {idx < value.length - 1 && <br />}
                </React.Fragment>
            ));
        }

        if (typeof value === 'string' && value.includes('\n')) {
            return <span style={{ whiteSpace: 'pre-wrap' }}>{value}</span>;
        }

        return value;
    };

    return (
        <div className={`info-panel ${className}`}>
            {items.map((item, index) => (
                <div key={`${item.label}-${index}`} className="info-row">
                    <div className="info-label">
                        {item.label}
                    </div>
                    <div className="info-value" style={{color: item.valueColor ?? "#000000"}}>
                        {renderValue(item.value)}
                        {item.valueIcon}
                        {item.info && (
                            <span className="info-panel-tooltip-wrapper">
                                <Info size={15} />
                                <span className="info-panel-tooltip">{item.info}</span>
                            </span>
                        )}
                        {item.onEdit && (
                            <button
                                type="button"
                                className="edit-field-icon"
                                onClick={item.onEdit}
                                title={item.editTitle ?? 'Ändra'}
                                aria-label={item.editTitle ?? 'Ändra'}
                                disabled={item.editDisabled}
                                style={item.editDisabled ? { opacity: 0.35, cursor: 'not-allowed' } : undefined}
                            >
                                <Pencil size={16} />
                            </button>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default InfoPanel;